import { isEqual } from 'lodash';
import {
    UpdateFeatureItemsFiltersAction,
    RemoveFeatureItemsFilterAction,
    UPDATE_FEATURE_ITEMS_FILTERS,
    REMOVE_FEATURE_ITEMS_FILTER,
    UpdateShowFixesOnlyAction,
    UPDATE_SHOW_FIXES_ONLY,
    PERFORM_FEATURE_ITEMS_SEARCH,
    UpdateFeatureItemsSearchAction,
    UPDATE_GROUP_WORK_ITEMS_BY_TICKET,
    UpdateGroupWorkItemsByTicketAction,
    UpdateShowAutomationsTimelineEventsOnlyAction,
    UPDATE_SHOW_AUTOMATIONS_TIMELINE_EVENTS_ONLY,
    UPDATE_TIMELINE_EVENTS_FILTERS,
    REMOVE_TIMELINE_EVENTS_FILTER,
    UpdateTimelineEventsFiltersAction,
    RemoveTimelineEventsFilterAction,
} from '../actions/filters';
import { LogoutSuccessAction, LOGOUT_SUCCESS } from '../actions/login';
import { UpdateApprovalsSearchAction, UPDATE_APPROVALS_SEARCH } from '../actions/approvalsSearch';
import { UpdateUsersSearchAction, UPDATE_USERS_SEARCH } from '../actions/userSearch';
import {
    UpdateApprovalsSortAction,
    UpdateFeatureFlagsSortAction,
    UpdateFeatureItemsSortAction,
    UPDATE_APPROVALS_SORT,
    UPDATE_FEATURE_FLAGS_SORT,
    UPDATE_FEATURE_ITEMS_SORT,
    UPDATE_AUTOMATIONS_TOOLBOX_SORT,
    UpdateAutomationsToolboxSortAction,
} from '../actions/sorts';
import { ClearStateAction, CLEAR_STATE } from '../actions/state';
import { AnyFilter } from '../types/filters';
import { ApprovalsSortType, AutomationsToolboxSortType, FeatureFlagsSortType, SortType } from '../types/sorts';
import { AnyTimelineEventsFilter } from '../types/timelineEventsFilters';

interface FiltersAndSortsStoreType {
    filters: AnyFilter[];
    sort: SortType;
    shouldShowFixesOnly: boolean;
    shouldGroupWorkItemsByTicket: boolean;
    query?: string;
}

const removeFeatureItemsFilter: (
    state: FiltersAndSortsStoreType,
    itemToBeRemoved: AnyFilter,
) => FiltersAndSortsStoreType = (state, itemToBeRemoved) => {
    return { ...state, filters: state.filters.filter((item) => !isEqual(item, itemToBeRemoved)) };
};

export type FilterAndSortActionTypes =
    | UpdateFeatureItemsFiltersAction
    | RemoveFeatureItemsFilterAction
    | LogoutSuccessAction
    | ClearStateAction
    | UpdateFeatureItemsSortAction
    | UpdateShowFixesOnlyAction
    | UpdateGroupWorkItemsByTicketAction
    | UpdateFeatureItemsSearchAction;

const initialFiltersAndSortsStoreType = {
    filters: [],
    sort: SortType.LastUpdated,
    shouldShowFixesOnly: false,
    shouldGroupWorkItemsByTicket: false,
};

const featureItemSearchAndFiltersAndSortReducer: (
    state: FiltersAndSortsStoreType,
    action: FilterAndSortActionTypes,
) => FiltersAndSortsStoreType = (state = initialFiltersAndSortsStoreType, action) => {
    switch (action.type) {
        case UPDATE_FEATURE_ITEMS_FILTERS: {
            return {
                ...state,
                filters: [...action.payload.filters],
            };
        }
        case UPDATE_FEATURE_ITEMS_SORT:
            return {
                ...state,
                sort: action.payload.sortType,
            };
        case PERFORM_FEATURE_ITEMS_SEARCH:
            return {
                ...state,
                query: action.payload,
            };
        case REMOVE_FEATURE_ITEMS_FILTER:
            return removeFeatureItemsFilter(state, action.payload);
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return initialFiltersAndSortsStoreType;
        case UPDATE_SHOW_FIXES_ONLY:
            return {
                ...state,
                shouldShowFixesOnly: action.payload,
            };
        case UPDATE_GROUP_WORK_ITEMS_BY_TICKET:
            return {
                ...state,
                shouldGroupWorkItemsByTicket: action.payload,
            };
        default:
            return state;
    }
};

interface AutomationsToolboxSortsStoreType {
    sort: AutomationsToolboxSortType;
}

export type AutomationsToolboxSortActionType = UpdateAutomationsToolboxSortAction;

const initialAutomationsToolboxSortsStoreType = {
    sort: AutomationsToolboxSortType.New,
};

export const automationsToolboxSortReducer: (
    state: AutomationsToolboxSortsStoreType,
    action: AutomationsToolboxSortActionType,
) => AutomationsToolboxSortsStoreType = (state = initialAutomationsToolboxSortsStoreType, action) => {
    switch (action.type) {
        case UPDATE_AUTOMATIONS_TOOLBOX_SORT:
            return {
                ...state,
                sort: action.payload.sortType,
            };
        default:
            return state;
    }
};

interface FeatureFlagsSortsStoreType {
    sort: FeatureFlagsSortType;
}

export type FeatureFlagsSortActionType = UpdateFeatureFlagsSortAction;

const initialFeatureFlagsSortsStoreType = {
    sort: FeatureFlagsSortType.Highlighted,
};

export const featureFlagsSortReducer: (
    state: FeatureFlagsSortsStoreType,
    action: FeatureFlagsSortActionType,
) => FeatureFlagsSortsStoreType = (state = initialFeatureFlagsSortsStoreType, action) => {
    switch (action.type) {
        case UPDATE_FEATURE_FLAGS_SORT:
            return {
                ...state,
                sort: action.payload.sortType,
            };
        default:
            return state;
    }
};

interface ApprovalsSortsAndSearchStoreType {
    sort: ApprovalsSortType;
    query: string;
}

export type ApprovalsSortAndSearchActionTypes = UpdateApprovalsSearchAction | UpdateApprovalsSortAction;

const initialApprovalsSortsAndSearchStoreType = {
    sort: ApprovalsSortType.Default,
    query: '',
};

export const approvalsSortAndSearchReducer: (
    state: ApprovalsSortsAndSearchStoreType,
    action: ApprovalsSortAndSearchActionTypes,
) => ApprovalsSortsAndSearchStoreType = (state = initialApprovalsSortsAndSearchStoreType, action) => {
    switch (action.type) {
        case UPDATE_APPROVALS_SORT:
            return {
                ...state,
                sort: action.payload.sortType,
            };
        case UPDATE_APPROVALS_SEARCH:
            return {
                ...state,
                query: action.payload.query,
            };
        default:
            return state;
    }
};

interface UsersSearchStoreType {
    query: string;
}

const initialUsersSearchStoreType = {
    query: '',
};

export const usersSearchReducer: (
    state: UsersSearchStoreType,
    action: UpdateUsersSearchAction,
) => UsersSearchStoreType = (state = initialUsersSearchStoreType, action) => {
    switch (action.type) {
        case UPDATE_USERS_SEARCH:
            return {
                ...state,
                query: action.payload.query,
            };
        default:
            return state;
    }
};

interface TimelineEventsFiltersStoreType {
    filters: AnyTimelineEventsFilter[];
    shouldShowAutomationsTimelineEventsOnly: boolean;
}

export type TimelineEventsFiltersActionTypes =
    | UpdateTimelineEventsFiltersAction
    | RemoveTimelineEventsFilterAction
    | LogoutSuccessAction
    | ClearStateAction
    | UpdateShowAutomationsTimelineEventsOnlyAction;

const initialTimelineEventsFiltersStoreType = {
    filters: [],
    shouldShowAutomationsTimelineEventsOnly: false,
};

const removeTimelineEventsFilter: (
    state: TimelineEventsFiltersStoreType,
    itemToBeRemoved: AnyTimelineEventsFilter,
) => TimelineEventsFiltersStoreType = (state, itemToBeRemoved) => {
    return { ...state, filters: state.filters.filter((event) => !isEqual(event, itemToBeRemoved)) };
};

export const timelineEventFiltersReducer: (
    state: TimelineEventsFiltersStoreType,
    action: TimelineEventsFiltersActionTypes,
) => TimelineEventsFiltersStoreType = (state = initialTimelineEventsFiltersStoreType, action) => {
    switch (action.type) {
        case UPDATE_TIMELINE_EVENTS_FILTERS:
            return {
                ...state,
                filters: [...action.payload.filters],
            };
        case REMOVE_TIMELINE_EVENTS_FILTER:
            return removeTimelineEventsFilter(state, action.payload);
        case UPDATE_SHOW_AUTOMATIONS_TIMELINE_EVENTS_ONLY:
            return {
                ...state,
                shouldShowAutomationsTimelineEventsOnly: action.payload,
            };
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return initialTimelineEventsFiltersStoreType;
        default:
            return state;
    }
};

export default featureItemSearchAndFiltersAndSortReducer;
